import React, { useState, Fragment, useEffect } from 'react';
import { makeStyles } from "@material-ui/core/styles";

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Alert from '@material-ui/lab/Alert';

import Link from 'app/components/Link';
import PasswordPatterns from 'app/utils/PasswordPatterns';
import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';
import useQuery from 'app/hooks/useQuery';

const useStyles = makeStyles(theme => ({
    submit: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    marginTop: {
        marginTop: theme.spacing(2),
    },
    passwordHeader: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    dividerTop: {
        marginTop: theme.spacing(1),
    },
    divider: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    betaClosed: {
        marginTop: theme.spacing(5),
        marginBottom: theme.spacing(5),
    }
}));

export default function UserRegistrationForm (props) {

    const [ email, setEmail ] = useState("");
    const [ username, setUsername ] = useState("");
    const [ first, setFirst ] = useState("");
    const [ last, setLast ] = useState("");
    const [ password, setPassword ] = useState("");
    const [ passwordConfirmation, setPasswordConfirmation ] = useState("");
    const [ errors, setErrors ] = useState({});
    const [ token, setToken ] = useState(null);

    const classes = useStyles();

    const { onLoading } = props;

    const api = useApi();
    const { app, hydrateApp, handleApp } = useApp();
    const query = useQuery();

    useEffect(() => {
        setToken(query.get('token'));
    }, [])

    const handleEmail = e => {

        const value = e.target.value;

        if (isError("email")) delete errors.email;

        setEmail(value);
    };

    const handleUsername = e => {

        const value = e.target.value;

        if (isError("username")) delete errors.username;

        setUsername(value);
    };

    const handleFirst= e => {

        const value = e.target.value;

        if (isError("first")) delete errors.first;

        setFirst(value);
    };

    const handleLast = e => {

        const value = e.target.value;

        if (isError("last")) delete errors.last;

        setLast(value);
    };

    const handlePassword = e => {

        const value = e.target.value;

        if (isError("password")) delete errors.password;

        setPassword(value);
    };

    const handlePasswordConfirmation = e => {

        const value = e.target.value;

        if (isError("password_confirmation")) delete errors.password_confirmation;

        setPasswordConfirmation(value);
    };

    const postUsernameValidate = () => {
        onLoading(true);

        const data = {
            username: username
        };

        api.post('/auth/username/validate', data).catch(error => {
            if (error?.response?.data?.errors) {
                setErrors(error.response.data.errors)
            }
        }).finally(() => onLoading(false));
    }

    const postRegistration = () => {
        onLoading(true);
        setErrors({});

        const data = {
            email: email,
            username: username,
            first: first,
            last: last,
            password: password,
            password_confirmation: passwordConfirmation,
            token: token
        }

        api.post(process.env.MIX_WEB_BASEURL + '/register', data).catch(error => {
            if (error?.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        }).finally(() => onLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const matchesPattern = pattern => {
        return pattern.test(password)
    }

    const getUsernameMessage = () => {
        if (isError('username')) return errors.username;
        return "Please do not use any spaces."
    }

    return ((process.env.MIX_TOKEN_REGISTRATION === 'true' && token) || ! (process.env.MIX_TOKEN_REGISTRATION === 'true')) ? (
        <div className="userRegistrationForm">
            <form noValidate>
                <Typography gutterBottom variant="subtitle1" align="center" color="textPrimary">
                    First, let's grab some info and verify your email address.
                </Typography>
                <Grid container spacing={3}>
                    <Grid item md={6}>
                        <TextField
                            id="input-email"
                            label="Email"
                            name="email"
                            value={email}
                            margin="normal"
                            onChange={handleEmail}
                            fullWidth
                            autoFocus
                            error={isError('email')}
                            helperText={getErrorMessage('email')}
                            variant="outlined"
                            color="secondary"
                        />
                        <TextField
                            id="input-username"
                            label="Username"
                            name="username"
                            value={username}
                            margin="normal"
                            onChange={handleUsername}
                            fullWidth
                            error={isError('username')}
                            helperText={getUsernameMessage()}
                            variant="outlined"
                            onBlur={postUsernameValidate}
                            color="secondary"
                        />
                        <Divider className={classes.dividerTop}/>
                        <TextField
                            id="input-first"
                            label="First Name"
                            name="first"
                            value={first}
                            margin="normal"
                            onChange={handleFirst}
                            fullWidth
                            error={isError('first')}
                            helperText={getErrorMessage('first')}
                            variant="outlined"
                            color="secondary"
                        />
                        <TextField
                            id="input-last"
                            label="Last Name"
                            name="last"
                            value={last}
                            margin="normal"
                            onChange={handleLast}
                            fullWidth
                            error={isError('last')}
                            helperText={getErrorMessage('last')}
                            variant="outlined"
                            color="secondary"
                        />
                        <Divider className={classes.dividerTop}/>
                        <TextField
                            id="input-password"
                            label="Password"
                            value={password}
                            onChange={handlePassword}
                            fullWidth
                            type="password"
                            error={isError('password')}
                            helperText={getErrorMessage('password')}
                            variant="outlined"
                            margin="normal"
                            color="secondary"
                        />
                        <TextField
                            id="input-check-password"
                            label="Confirm Password"
                            value={passwordConfirmation}
                            onChange={handlePasswordConfirmation}
                            fullWidth
                            type="password"
                            variant="outlined"
                            margin="normal"
                            color="secondary"
                            error={isError('password_confirmation')}
                            helperText={getErrorMessage('password_confirmation')}
                        />
                    </Grid>
                    <Grid item md={1} />
                    <Grid item md={5}>
                        <Typography className={classes.passwordHeader} gutterBottom variant="body2" color="textSecondary">
                            We use email to secure your account and provide platform services.
                        </Typography>
                        <Typography className={classes.passwordHeader} gutterBottom variant="body2" color="textSecondary">
                            Your username can be used to login and must be unique to this account.
                        </Typography>
                        <Typography className={classes.passwordHeader} gutterBottom variant="body2" color="textSecondary">
                            We use your name to properly address you on the platform and in communications.
                        </Typography>
                        <Typography className={classes.passwordHeader} gutterBottom variant="body2" color="textSecondary">
                            Your password must:
                        </Typography>
                        {PasswordPatterns.map((p, i) => (
                            <Fragment key={i}>
                                <Typography
                                    gutterBottom
                                    variant="body2"
                                    color={matchesPattern(p.regex) ? 'primary' : 'error'}
                                    >
                                    {p.label}
                                </Typography>
                                { (i < (PasswordPatterns.length - 1)) &&
                                    <Divider className={classes.divider}/>
                                }
                            </Fragment>
                        ))}
                    </Grid>
                </Grid>
                <div className={classes.marginTop}/>
                <Grid container spacing={3} wrap="nowrap" direction="row-reverse">
                    <Grid item md={5}>
                        <Button
                            variant="contained"
                            color="secondary"
                            fullWidth
                            onClick={postRegistration}
                            >
                            Next
                        </Button>
                    </Grid>
                    <Grid item md={1} />
                    <Grid item md={6}>
                        <Typography variant="body2" color="textSecondary">
                            By continuing, you are agreeing to our <Link to={process.env.MIX_LEGAL_BASEURL + process.env.MIX_LEGAL_ROUTE_USER} color="primary" target="_blank">User Terms of Service</Link> and our <Link to={process.env.MIX_LEGAL_BASEURL + process.env.MIX_LEGAL_ROUTE_PRIVACY} color="primary" target="_blank">Privacy Policy</Link>.
                        </Typography>
                    </Grid>
                </Grid>
            </form>
            {isError('general') &&
                <Alert severity="error" className={classes.marginTop}>
                    {getErrorMessage('general')}
                </Alert>
            }
            <Typography component="div" align="center" variant="subtitle2" className={classes.marginTop}>
                <Link to="/auth/login" color="primary" route>
                    Already have an Account?
                </Link>
            </Typography>
        </div>
    ) : (
        <>
            <Alert severity="error" className={classes.betaClosed}>
                Public registration is currently closed. If you would like an invitation to our private beta, please email our sales team at <a href="mailto:sales@clubapps.org">sales@clubapps.org</a>.
            </Alert>
            <Typography
                component="div"
                align="center"
                variant="subtitle2"
                >
                <Link to="/auth/login" color="primary" route>
                    Already have an Account?
                </Link>
            </Typography>
        </>
    );
}
